function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  return str.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  })[c]);
}

function buildJobBlock(title, company, date, bullets, tech) {
  const techLine = tech
    ? `<div class="job-tech">Technologies: ${escapeHtml(tech)}</div>`
    : '';
  return `
    <div class="job-block">
      <div class="job-header">
        <span class="job-title">${escapeHtml(title)}</span>
        <span class="job-date">${escapeHtml(date)}</span>
      </div>
      <div class="job-company">${escapeHtml(company)}</div>
      <ul class="list">${bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join('')}</ul>
      ${techLine}
    </div>
  `;
}

function buildEduBlock(title, institution, date, bullets) {
  return `
    <div class="job-block">
      <div class="job-header">
        <span class="job-title">${escapeHtml(title)}</span>
        <span class="job-date">${escapeHtml(date)}</span>
      </div>
      <div class="job-company">${escapeHtml(institution)}</div>
      <ul class="list">${bullets.map((b) => `<li>${escapeHtml(b)}</li>`).join('')}</ul>
    </div>
  `;
}

function buildProjectBlock(name, desc, tech, repo) {
  return `
    <div class="job-block">
      <div class="job-header">
        <span class="job-title">${escapeHtml(name)}</span>
      </div>
      <div class="job-company">${escapeHtml(desc)}</div>
      <div class="job-tech">Technologies: ${escapeHtml(tech)}</div>
      <div class="project-link">
        <a href="https://github.com/Stijnvandenbroek/${escapeHtml(repo)}" target="_blank" rel="noopener noreferrer">github.com/Stijnvandenbroek/${escapeHtml(repo)}</a>
      </div>
    </div>
  `;
}

function buildTagRow(tags) {
  return `
    <div class="tags">
      ${tags.map((t) => `<span class="tag">${escapeHtml(t)}</span>`).join('')}
    </div>
  `;
}

export function createCommands(ctx) {
  const { print, printLine, appendHtml, clearOutput, toggleMatrix, reboot, terminate } = ctx;

  const commandTable = {
    help: {
      desc: 'show available commands',
      run: () => {
        print('');
        printLine('AVAILABLE COMMANDS', 'cyan');
        const keys = [
          'help',
          'about',
          'experience',
          'education',
          'projects',
          'skills',
          'hobbies',
          'contact',
          'matrix',
          'clear',
          'reboot',
          'exit',
        ];
        const pad = keys.reduce((a, b) => Math.max(a, b.length), 0);
        keys.forEach((cmd) => {
          print(`  ${cmd.padEnd(pad)}  ${commandTable[cmd].desc}`, 'dim');
        });
        print('');
        print('Use ↑ / ↓ to recall commands. Tab to autocomplete.', 'dim');
      },
    },

    about: {
      desc: 'short bio and current role',
      run: () => {
        print('');
        printLine('ABOUT', 'cyan');
        print('I help data teams ship reliable, production-grade systems. As a Data');
        print('Science Consultant and Technical Trainer, I have worked with');
        print('organizations like ASN Bank, Alliander, Eneco, and Coolblue to modernize');
        print('their data stacks from fragmented on-prem notebooks to cloud-native');
        print('MLOps pipelines with CI/CD, infrastructure as code, and proper data');
        print('governance.');
        print('');
        print('I hold an MSc in Applied Data Science (8.2/10) from Utrecht University.');
        print('Outside of work, I am usually building something: a split mechanical');
        print('keyboard, a self-hosted homelab running on Proxmox, or a smart plant');
        print('monitoring system with Home Assistant. I also enjoy photography with my');
        print('Nikon Z5.', 'dim');
        print('');
        const startDate = new Date(2021, 8, 1);
        const now = new Date();
        const diffInMonths = (now.getFullYear() - startDate.getFullYear()) * 12
          + (now.getMonth() - startDate.getMonth());
        const years = Math.floor(diffInMonths / 12);
        print(`  ${years}+ years experience  MSc Data Science  Top project 19 stars`, 'dim');
      },
    },

    experience: {
      desc: 'work history and roles',
      run: () => {
        print('');
        printLine('WORK EXPERIENCE', 'cyan');
        appendHtml(buildJobBlock(
          'Data Science Consultant',
          'Navara',
          'October 2022 — Present',
          [
            'Present internal and external knowledge-sharing sessions on data engineering, machine learning, and AI practices.',
            'Contribute to project proposals and technical solutions for potential clients.',
            'Provide dbt training to colleagues and client teams.',
            'Achieve annual technical certifications including Kubernetes & Cloud Native Associate, SnowPro Core, and SnowPro Advanced Architect.',
          ],
          null
        ));
        appendHtml(buildJobBlock(
          'Data Platform Engineer',
          'ASN Bank',
          'May 2026 — Present',
          [
            'Architect and maintain scalable data pipelines using dbt and Snowflake.',
            'Implement data governance and cataloging solutions with Collibra.',
            'Build interactive dashboards and visualizations in Tableau for business stakeholders.',
            'Manage cloud infrastructure with Terraform and AWS services.',
            'Establish CI/CD pipelines and version control best practices with GitLab.',
          ],
          'dbt, Snowflake, Collibra, Tableau, Terraform, AWS, GitLab'
        ));
        appendHtml(buildJobBlock(
          'Technical Trainer',
          'Harvest',
          'February 2026 — Present',
          [
            'Deliver hands-on dbt training to professionals from companies including ASN Bank and Eneco.',
            'Design and facilitate interactive labs and real-world case studies.',
            'Help data teams adopt dbt best practices for analytics engineering workflows.',
          ],
          'dbt'
        ));
        appendHtml(buildJobBlock(
          'Data Science Software Engineer',
          'Alliander',
          'February 2023 — April 2026',
          [
            'Professionalize the data science team from fragmented on-prem workflows to a modern, software-driven cloud stack.',
            'Implement complete CI/CD setup with devcontainers, version control, automated testing, and AWS deployments.',
            'Design and implement scalable MLOps pipelines with SageMaker, Docker, and Kubernetes.',
            'Introduce workflow and data orchestration with Dagster, dbt, and Snowflake.',
            'Build reusable Python and R libraries to improve development speed and consistency.',
            'Containerize, refactor, and make existing model codebases cloud-native.',
          ],
          'Python, R, SQL, AWS, SageMaker, Dagster, dbt, Snowflake, GitHub Actions, Docker, Kubernetes, SAP HANA'
        ));
        appendHtml(buildJobBlock(
          'Data Engineer',
          'Eneco',
          'November 2022 — February 2023',
          [
            'Contribute to migration from monolithic platform to microservices architecture.',
            'Develop independently deployable modules for four internal departments within the heat chain.',
            'Develop and manage ETL processes and geodata processing.',
            'Deliver visualizations and full-stack functionality in Shiny and Mapbox.',
          ],
          'R, Python, Azure, Azure DevOps, Airflow, Shiny, Mapbox, PostgreSQL, Snowflake'
        ));
        appendHtml(buildJobBlock(
          'Data Developer',
          'Coolblue',
          'October 2021 — October 2022',
          [
            'Develop robust ETL processes for data sources such as Google reviews, market data, and warehouse data.',
            'Improve data reliability and accessibility for analysts.',
            'Conduct structural code reviews to ensure quality of data products.',
            'Build proof-of-concepts, including decision-tree pipelines with Google Dataproc for shipping insurance.',
          ],
          'Python, SQL (BigQuery), Airflow, Google Cloud Platform, TeamCity'
        ));
      },
    },

    education: {
      desc: 'education and degrees',
      run: () => {
        print('');
        printLine('EDUCATION', 'cyan');
        appendHtml(buildEduBlock(
          'MSc Applied Data Science',
          'Utrecht University',
          'July 2021',
          [
            'Average grade: 8.2/10',
            'Focus on spatial data, network analysis, and machine learning',
            'Thesis: "Synthetic Data Sets for the Analysis of Private Data" - Grade: 8.2',
          ]
        ));
        appendHtml(buildEduBlock(
          'BSc Information Science',
          'Utrecht University',
          'July 2020',
          [
            'Average grade: 7.1/10',
            'Specialization in data analytics and business IT',
          ]
        ));
      },
    },

    projects: {
      desc: 'selected GitHub repositories',
      run: () => {
        print('');
        printLine('SELECTED REPOSITORIES', 'cyan');
        print('');
        appendHtml(buildProjectBlock(
          'Data Platform',
          'Complete data platform solution orchestrating data pipelines with Dagster, transforming data with dbt, tracking ML experiments with MLFlow, and storing everything in PostgreSQL.',
          'Python, Dagster, dbt, MLFlow, PostgreSQL',
          'data-platform'
        ));
        appendHtml(buildProjectBlock(
          'Age of OpenCode',
          'OpenCode plugin that plays Age of Empires II sound effects on lifecycle events. 46 authentic audio cues for session start, prompts, completions, and errors.',
          'Python, AI Agent, OpenCode, Automation',
          'age-of-opencode'
        ));
        appendHtml(buildProjectBlock(
          'House ELO',
          'Frontend application for subjective rankings of Funda (Dutch real estate) listings. Implements ELO rating system to help compare and rank properties based on personal preferences.',
          'Python, Frontend, ELO Algorithm',
          'house-elo-ranking'
        ));
        appendHtml(buildProjectBlock(
          'Cyberpunk Keyboard',
          'Complete keymap and OLED animation system for Corne split keyboards. Features cyberpunk-themed visuals and animations. My most starred project with 19 stars and 4 forks.',
          'C, QMK Firmware, Hardware, OLED',
          'cyberpunk-corne'
        ));
        appendHtml(buildProjectBlock(
          'Stamp Quiz App',
          'A quiz application designed for professionals. Built with modern JavaScript and focused on creating engaging, interactive learning experiences.',
          'JavaScript, Frontend, Quiz App',
          'stamp'
        ));
        print('');
        print('See all repositories at https://github.com/Stijnvandenbroek', 'dim');
      },
    },

    skills: {
      desc: 'technologies and tools',
      run: () => {
        print('');
        printLine('SKILLS & TECHNOLOGIES', 'cyan');
        print('');
        printLine('Cloud & MLOps', 'magenta');
        appendHtml(buildTagRow([
          'AWS (SageMaker, ECS, Lambda)',
          'Azure',
          'GCP',
          'Kubernetes',
          'Docker',
          'GitHub Actions',
          'CI/CD',
        ]));
        print('');
        printLine('Data Engineering', 'magenta');
        appendHtml(buildTagRow([
          'Python',
          'R',
          'SQL',
          'Dagster',
          'dbt',
          'Snowflake',
          'PostgreSQL',
          'Airflow',
        ]));
        print('');
        printLine('Frontend & Other', 'magenta');
        appendHtml(buildTagRow([
          'JavaScript',
          'React',
          'HTML5 & CSS3',
          'Shiny',
          'Git & GitHub',
          'C / Embedded',
          'QMK Firmware',
        ]));
      },
    },

    hobbies: {
      desc: 'genuine hobbies and interests',
      run: () => {
        print('');
        printLine('INTERESTS', 'cyan');
        print('');
        printLine('Open source keyboards', 'magenta');
        print('  Corne with IMK case, dual OLED screens, lubed + filmed Boba U4T');
        print('  switches, KAM Superuser keycaps');
        print('  Planck in walnut case with copper plate and Holy Panda switches');
        print('  Low profile Corne with Kailh Choc burnt orange switches for work');
        print('  All programmed with Colemak DHm layout');
        print('');
        printLine('Homelab & self-hosted infrastructure', 'magenta');
        print('  OPNsense router for network management');
        print('  Nginx reverse proxy + Pi-hole DNS filtering');
        print('  Self-hosted dashboard, file storage, media servers');
        print('  Home Assistant for smart home automation');
        print('  TeslaMate for Tesla tracking');
        print('  Self-hosted data platform: dbt, Dagster, MLflow, PostgreSQL');
        print('  Personal Hermes agent for managing the entire stack');
        print('  Docker containers orchestrated across Proxmox VMs');
        print('');
        printLine('Houseplants', 'magenta');
        print('  Soil moisture, light, temperature & humidity sensors');
        print('  Sensor data combined with Open Plantbook for optimal care');
        print('');
        printLine('Photography', 'magenta');
        print('  Nikon D3500 with 35mm f/1.8 and 50mm f/1.8 lenses');
        print('  Nikon Z5 for mirrorless photography');
        print('');
        printLine('Virtual reality', 'magenta');
        print('  Oculus Rift');
        print('  Meta Quest 2');
        print('  Meta Quest Pro');
        print('  Sim racing setup');
      },
    },

    contact: {
      desc: 'contact links and socials',
      run: () => {
        print('');
        printLine('CONTACT', 'cyan');
        print('I am always interested in discussing MLOps, data engineering, cloud');
        print('infrastructure, or creative coding opportunities. Let us build something');
        print('together.');
        print('');
        appendHtml(`
          <div class="contact-list">
            <div class="contact-row">
              <span class="contact-key">LinkedIn</span>
              <a href="https://www.linkedin.com/in/broekvandens/" aria-label="LinkedIn" target="_blank" rel="noopener noreferrer">linkedin.com/in/broekvandens</a>
            </div>
            <div class="contact-row">
              <span class="contact-key">GitHub</span>
              <a href="https://github.com/Stijnvandenbroek" aria-label="GitHub" target="_blank" rel="noopener noreferrer">github.com/Stijnvandenbroek</a>
            </div>
          </div>
        `);
      },
    },

    matrix: {
      desc: 'toggle the digital rain overlay',
      run: () => {
        const isOn = toggleMatrix();
        print('');
        printLine(isOn ? 'MATRIX RAIN: ON' : 'MATRIX RAIN: OFF', 'green');
      },
    },

    clear: {
      desc: 'clear the terminal',
      run: () => {
        clearOutput();
      },
    },

    reboot: {
      desc: 'restart the terminal',
      run: () => {
        print('');
        printLine('REBOOTING...', 'error');
        setTimeout(() => {
          reboot();
        }, 600);
      },
    },

    exit: {
      desc: 'close terminal session',
      run: () => {
        print('');
        printLine('Session terminated. Refresh the page to reconnect.', 'error');
        terminate();
      },
    },
  };

  return commandTable;
}
